/* 
Generated code: do not modify this file as it will be overwritten
Template: ImmutableJSRecord.cst

Known Issues/TODO:
- Using *Props type as Record constructor parameter is insufficient.
If a property needs to be a Record type, it should already be constructed.
- Rename Record fromJS factory method to fromJSON
- Create parseJSON method that translates JSON to Props



 */

/* eslint-disable import/order */

import { createSelector } from '@ngrx/store';
import { List, Map, Record, Seq, Set } from 'immutable';
import { AppState } from 'src/store/appstate';
import { getFromOrThrow, propValueOrDefault, tryOrDefault } from 'src/util/values';

export interface NonReferralReasonProps {
  id: string;
  name: string;
  associatedCancellationReasonId: string | null;
  associatedCancellationSecondaryReasonId: string | null;
  dateLastUpdated: Date;
  dateCreated: Date;
  dateDeleted: Date | null;
  lastUpdatedById: string | null;
}

export class NonReferralReasonRecord extends Record<NonReferralReasonProps>({
  id: null as any,
  name: null as any,
  associatedCancellationReasonId: null as any,
  associatedCancellationSecondaryReasonId: null as any,
  dateLastUpdated: null as any,
  dateCreated: null as any,
  dateDeleted: null as any,
  lastUpdatedById: null as any,
}) {
  public static fromJS(json: {
    id: string;
    name: string;
    associatedCancellationReasonId: string | null;
    associatedCancellationSecondaryReasonId: string | null;
    dateLastUpdated: string | number | Date;
    dateCreated: string | number | Date;
    dateDeleted: string | number | Date | null;
    lastUpdatedById: string | null;
  }): NonReferralReasonRecord {
    if (json == null) {
      throw new Error('NonReferralReasonRecord.fromJS: parameter cannot be null');
    }
    return new NonReferralReasonRecord({
      id: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'id', '00000000-0000-0000-0000-000000000000'),
        'NonReferralReasonJson.id',
      ),
      name: getFromOrThrow(json, (j) => j.name, 'NonReferralReasonJson.name'),
      associatedCancellationReasonId: json.associatedCancellationReasonId,
      associatedCancellationSecondaryReasonId: json.associatedCancellationSecondaryReasonId,
      dateLastUpdated: new Date(json.dateLastUpdated),
      dateCreated: new Date(json.dateCreated),
      dateDeleted: json.dateDeleted != null ? new Date(json.dateDeleted) : null,
      lastUpdatedById: json.lastUpdatedById,
    });
  }

  public static fromJSUnsafe(json: {
    id: string;
    name: string;
    associatedCancellationReasonId: string | null;
    associatedCancellationSecondaryReasonId: string | null;
    dateLastUpdated: string | number | Date;
    dateCreated: string | number | Date;
    dateDeleted: string | number | Date | null;
    lastUpdatedById: string | null;
  }): [NonReferralReasonRecord, Error[]] {
    if (json == null) {
      throw new Error('NonReferralReasonRecord.fromJSUnsafe: parameter cannot be null');
    }
    const errors: Error[] = [];

    const record = new NonReferralReasonRecord({
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      id: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'id', '00000000-0000-0000-0000-000000000000'),
            'NonReferralReasonJson.id',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      name: tryOrDefault(
        () => getFromOrThrow(json, (j) => j.name, 'NonReferralReasonJson.name'),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      associatedCancellationReasonId: tryOrDefault(
        () => json.associatedCancellationReasonId,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      associatedCancellationSecondaryReasonId: tryOrDefault(
        () => json.associatedCancellationSecondaryReasonId,
        (err) => errors.push(err),
      )!,
      dateLastUpdated: new Date(json.dateLastUpdated),
      dateCreated: new Date(json.dateCreated),
      dateDeleted: json.dateDeleted != null ? new Date(json.dateDeleted) : null,
      lastUpdatedById: json.lastUpdatedById,
    });

    return [record, errors];
  }

  constructor(props: NonReferralReasonProps) {
    super(props);
    if (props == null) {
      throw new Error('NonReferralReasonRecord constructor parameter cannot be null');
    }
  }
}

export abstract class NonReferralReasonViewModelBase implements NonReferralReasonProps {
  protected readonly _props: NonReferralReasonProps;
  private _json?: NonReferralReasonProps;

  get id(): string {
    return this._props.id;
  }
  get name(): string {
    return this._props.name;
  }
  get associatedCancellationReasonId(): string | null {
    return this._props.associatedCancellationReasonId;
  }
  get associatedCancellationSecondaryReasonId(): string | null {
    return this._props.associatedCancellationSecondaryReasonId;
  }

  get dateCreated(): Date {
    return this._props.dateCreated;
  }
  get dateLastUpdated(): Date {
    return this._props.dateLastUpdated;
  }
  get dateDeleted(): Date | null {
    return this._props.dateDeleted;
  }
  get lastUpdatedById(): string | null {
    return this._props.lastUpdatedById;
  }

  constructor(props: NonReferralReasonProps) {
    this._props = props;
  }

  public toJS() {
    return this.toJSON();
  }

  public toJSON() {
    if (this._json == null) {
      this._json = {
        id: this._props.id,
        name: this._props.name,
        associatedCancellationReasonId: this._props.associatedCancellationReasonId,
        associatedCancellationSecondaryReasonId:
          this._props.associatedCancellationSecondaryReasonId,
        dateLastUpdated: this._props.dateLastUpdated,
        dateCreated: this._props.dateCreated,
        dateDeleted: this._props.dateDeleted,
        lastUpdatedById: this._props.lastUpdatedById,
      };
    }
    return this._json;
  }
}

//#region Selectors

export const selectAllNonReferralReasonEntities = (state: AppState) =>
  state.entityStores.nonReferralReason.entities;

export const selectNonReferralReasonById = (id: string) => (state: AppState) =>
  state.entityStores.nonReferralReason.entities.get(id);

export const selectNonReferralReasonMetadataById = (id: string) => (state: AppState) =>
  state.entityStores.nonReferralReason.meta.get(id);

export const selectNonReferralReasonAndMetadataById = (id: string) =>
  createSelector(
    selectNonReferralReasonById(id),
    selectNonReferralReasonMetadataById(id),
    (nonReferralReason, meta) => ({ nonReferralReason, meta }),
  );

export function selectNonReferralReasonEntitiesByIds(ids: string[]) {
  return createSelector(
    selectAllNonReferralReasonEntities,

    (entities) => {
      // TODO: Once we're on TS 3.5+, I don't think we'll need use type assertions
      const entries = ids.map((id) => [id, entities.get(id)]) as unknown as Iterable<
        [string, NonReferralReasonRecord | undefined]
      >;
      return Map(entries);
    },
  );
}

export function selectNonReferralReasonEntitiesByName(name: string) {
  return createSelector(selectAllNonReferralReasonEntities, (entities) =>
    entities.filter((e) => e.name === name).valueSeq(),
  );
}

export function selectNonReferralReasonEntitiesByAssociatedCancellationReasonId(
  associatedCancellationReasonId: string,
) {
  return createSelector(selectAllNonReferralReasonEntities, (entities) =>
    entities
      .filter((e) => e.associatedCancellationReasonId === associatedCancellationReasonId)
      .valueSeq(),
  );
}

export function selectNonReferralReasonEntitiesByAssociatedCancellationSecondaryReasonId(
  associatedCancellationSecondaryReasonId: string,
) {
  return createSelector(selectAllNonReferralReasonEntities, (entities) =>
    entities
      .filter(
        (e) =>
          e.associatedCancellationSecondaryReasonId === associatedCancellationSecondaryReasonId,
      )
      .valueSeq(),
  );
}
//#endregion Selectors
